import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getMajorBook } from '../../../../../../lib/api/accounting';
import GlobalSearcher from '../../../../../../lib/common/GlobalSearcher';
import CustomButton from '../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions from '../../../../../../lib/designSystem/HeaderWithActions';
import MajorBookEntriesTable from './components';

dayjs.extend(utc);

import ExcelJS from 'exceljs';
import { formatDateToLocal } from 'lib/designSystem/Table/cellFormat';

interface responseType {
  majorBookAccounts: {
    groupedLines: {
      date: string;
      businessIdentifier: string;
      businessName: string;
    }[];
  }[];
}

const exportToExcel = async (
  entries: responseType | null,
  startDate: Date,
  endDate: Date,
) => {
  if (!entries) return;
  // Mapeo de datos planos
  const plainData = entries.majorBookAccounts.flatMap((category: any) => {
    return category.groupedLines.map((line: any) => {
      return [
        line.date, // Fecha
        line.businessIdentifier, // Rut cliente
        line.businessName, // Nombre cliente
        line.account, // Cuenta
        line.accountName, // Nombre de Cuenta
        line.description, // Descripción
        line.documentId, // Documento
        line.debit, // Débito
        line.credit, // Crédito
        line.debitFinal, // Débito final
        line.creditFinal, // Crédito final
      ];
    });
  });

  // Encabezados de la hoja de Excel
  const headers = [
    'Fecha',
    'Rut cliente',
    'Nombre cliente',
    'Cuenta',
    'Nombre de Cuenta',
    'Descripción',
    'Documento',
    'Debe',
    'Haber',
    'Débito',
    'Crédito',
  ];

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Sheet1');

  // Agregar datos a la hoja
  [headers, ...plainData].forEach((rowData, rowIndex) => {
    const row = worksheet.addRow(rowData);

    if (rowIndex === 0) {
      // Formato para la primera fila (encabezados)
      row.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        worksheet.getColumn(cell.col).width = 20; // Ajustar ancho de columna
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFDDDDDD' }, // Color gris claro
        };
      });
    }
  });

  // Guardar el archivo
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);

  // Descargar el archivo
  const a = document.createElement('a');
  a.href = url;
  a.download = `Libro Mayor ${formatDateToLocal(startDate)} - ${formatDateToLocal(endDate)}.xlsx`;
  a.click();

  URL.revokeObjectURL(url);
};

const MajorBook: React.FC<{
  transactional?: boolean;
  startDate?: Date;
  endDate?: Date;
}> = ({
  transactional = false,
  startDate: propStartDate,
  endDate: propEndDate,
}) => {
  const [data, setData] = useState<responseType | null>(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();

  // Initialize with current month
  const now = dayjs();
  const startDateDefault = now.startOf('month').toDate();
  const endDateDefault = now.endOf('month').toDate();

  const [startDate, setStartDate] = useState<string | null>(
    propStartDate
      ? propStartDate.toISOString().split('T')[0]
      : startDateDefault.toISOString().split('T')[0],
  );
  const [endDate, setEndDate] = useState<string | null>(
    propEndDate
      ? propEndDate.toISOString().split('T')[0]
      : endDateDefault.toISOString().split('T')[0],
  );

  const setUp = async (startDateParam: string, endDateParam: string) => {
    setStartDate(startDateParam);
    setEndDate(endDateParam);
    const queryParams = `startDate=${startDateParam}&endDate=${endDateParam}&transactional=${transactional}`;
    try {
      setLoading(true);
      const response = await getMajorBook(dispatch, queryParams);
      setData(response);
    } catch (error) {
      console.error('Error fetching major book:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (propStartDate && propEndDate) {
      // Use props if available
      const startDateString = propStartDate.toISOString().split('T')[0];
      const endDateString = propEndDate.toISOString().split('T')[0];
      void setUp(startDateString, endDateString);
    } else {
      // Use default dates for initial load
      void setUp(
        formatDateToLocal(startDateDefault).split('T')[0],
        formatDateToLocal(endDateDefault).split('T')[0],
      );
    }
  }, [propStartDate, propEndDate]);

  if (loading) return <CenteredCircularProgress type="layout" />;
  return (
    <div>
      <div>
        <HeaderWithActions
          title={`Período: ${startDate ? dayjs(startDate).format('DD/MM/YYYY') : ''} - ${endDate ? dayjs(endDate).format('DD/MM/YYYY') : ''}`}
          variant="body1"
          item={
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}
            >
              <div>
                <GlobalSearcher
                  filters={[
                    {
                      type: 'date',
                      label: 'Fecha de inicio',
                      dateKey: 'startDate',
                      value: startDate,
                      setter: setStartDate,
                    },
                    {
                      type: 'date',
                      label: 'Fecha de fin',
                      dateKey: 'endDate',
                      value: endDate,
                      setter: setEndDate,
                    },
                  ]}
                  triggerSearch={async () => {
                    if (startDate && endDate) {
                      await setUp(startDate, endDate);
                    }
                  }}
                  shortcuts={true}
                />
              </div>
              <div>
                <CustomButton
                  color="primary"
                  onClick={() => {
                    if (startDate && endDate) {
                      void exportToExcel(
                        data,
                        new Date(startDate),
                        new Date(endDate),
                      );
                    }
                  }}
                >
                  Exportar
                </CustomButton>
              </div>
            </div>
          }
        />
      </div>
      <div style={{ marginTop: 20 }}>
        <MajorBookEntriesTable entries={data} />
      </div>
    </div>
  );
};

export default MajorBook;
